import { pokerCardsOnly } from './cards.js';

/** @typedef {import('./cards.js').Card} Card */

const CAT_HIGH = 1;
const CAT_PAIR = 2;
const CAT_TWO_PAIR = 3;
const CAT_TRIPS = 4;
const CAT_STRAIGHT = 5;
const CAT_FLUSH = 6;
const CAT_FULL = 7;
const CAT_QUADS = 8;
const CAT_STR_FLUSH = 9;

/**
 * Best 5-card hand from 7 cards. Returns { category, ranks: number[] } for lexicographic compare.
 * @param {Card[]} seven
 */
export function evaluate7(seven) {
  const cards = pokerCardsOnly(seven || []);
  const n = cards.length;
  if (n < 5) {
    return { category: CAT_HIGH, ranks: [0, 0, 0, 0, 0] };
  }
  let best = null;
  const comb = [];
  function dfs(start, depth) {
    if (depth === 5) {
      const h = comb.map((i) => cards[i]);
      const e = evaluate5(h);
      if (!best || compareHands(e, best) > 0) best = e;
      return;
    }
    for (let i = start; i < n; i += 1) {
      comb.push(i);
      dfs(i + 1, depth + 1);
      comb.pop();
    }
  }
  dfs(0, 0);
  return best;
}

/** @param {{ category: number, ranks: number[] }} a @param {{ category: number, ranks: number[] }} b */
export function compareHands(a, b) {
  if (a.category !== b.category) return a.category - b.category;
  for (let i = 0; i < Math.max(a.ranks.length, b.ranks.length); i += 1) {
    const x = a.ranks[i] || 0;
    const y = b.ranks[i] || 0;
    if (x !== y) return x - y;
  }
  return 0;
}

/**
 * @param {Card[]} five
 * @returns {{ category: number, ranks: number[] }}
 */
function evaluate5(five) {
  const ranks = five.map((c) => c.rank).sort((x, y) => y - x);
  const suits = five.map((c) => c.suit);
  const rankCounts = {};
  for (const r of ranks) {
    rankCounts[r] = (rankCounts[r] || 0) + 1;
  }
  const counts = Object.entries(rankCounts)
    .map(([r, n]) => ({ rank: Number(r), n }))
    .sort((a, b) => (b.n - a.n) || (b.rank - a.rank));

  const isFlush = suits.every((s) => s === suits[0]);
  const straightHigh = straightHighRank(ranks);

  if (isFlush && straightHigh) {
    return { category: CAT_STR_FLUSH, ranks: [straightHigh] };
  }
  if (counts[0].n === 4) {
    const kicker = counts[1].rank;
    return { category: CAT_QUADS, ranks: [counts[0].rank, kicker] };
  }
  if (counts[0].n === 3 && counts[1].n === 2) {
    return { category: CAT_FULL, ranks: [counts[0].rank, counts[1].rank] };
  }
  if (isFlush) {
    return { category: CAT_FLUSH, ranks: ranks.slice() };
  }
  if (straightHigh) {
    return { category: CAT_STRAIGHT, ranks: [straightHigh] };
  }
  if (counts[0].n === 3) {
    const kickers = counts.slice(1).map((c) => c.rank).sort((a, b) => b - a);
    return { category: CAT_TRIPS, ranks: [counts[0].rank, ...kickers] };
  }
  if (counts[0].n === 2 && counts[1].n === 2) {
    const hi = Math.max(counts[0].rank, counts[1].rank);
    const lo = Math.min(counts[0].rank, counts[1].rank);
    const kicker = counts[2].rank;
    return { category: CAT_TWO_PAIR, ranks: [hi, lo, kicker] };
  }
  if (counts[0].n === 2) {
    const kickers = counts.slice(1).map((c) => c.rank).sort((a, b) => b - a);
    return { category: CAT_PAIR, ranks: [counts[0].rank, ...kickers] };
  }
  return { category: CAT_HIGH, ranks: ranks.slice() };
}

/**
 * @param {number[]} ranksSortedDesc unique-ish from 5 cards
 * @returns {number|null} high card of straight or null
 */
function straightHighRank(ranksSortedDesc) {
  const uniq = [...new Set(ranksSortedDesc)].sort((a, b) => b - a);
  if (uniq.includes(14) && uniq.includes(5) && uniq.includes(4) && uniq.includes(3) && uniq.includes(2)) {
    return 5;
  }
  for (let i = 0; i <= uniq.length - 5; i += 1) {
    let ok = true;
    for (let k = 0; k < 4; k += 1) {
      if (uniq[i + k] - uniq[i + k + 1] !== 1) {
        ok = false;
        break;
      }
    }
    if (ok) return uniq[i];
  }
  return null;
}

export function handCategoryLabel(cat) {
  const labels = {
    [CAT_HIGH]: 'High',
    [CAT_PAIR]: 'Pair',
    [CAT_TWO_PAIR]: 'Two pair',
    [CAT_TRIPS]: 'Trips',
    [CAT_STRAIGHT]: 'Straight',
    [CAT_FLUSH]: 'Flush',
    [CAT_FULL]: 'Full house',
    [CAT_QUADS]: 'Quads',
    [CAT_STR_FLUSH]: 'Str. flush',
  };
  return labels[cat] || '';
}
